"use client";

import { CcResponsivePoster } from "@/components/services/shared/CcResponsivePoster";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcHeroScrollParallax } from "@/lib/use-cc-hero-scroll-parallax";

export type CcProjectsHeroLayout = "default" | "centered";

type CcProjectsHeroProps = {
  eyebrow: string;
  title: string;
  description: string;
  /** Full-width photo backdrop (services overview, etc.). */
  backdropSrc?: string;
  /** Centered stack (home gallery-petrol parity); default is bottom-left copy column. */
  layout?: CcProjectsHeroLayout;
};

export function CcProjectsHero({
  eyebrow,
  title,
  description,
  backdropSrc,
  layout = "default",
}: CcProjectsHeroProps) {
  const sectionRef = useCcHeroScrollParallax();
  const hasPhotoBackdrop = Boolean(backdropSrc);
  const layoutClass =
    layout === "centered" ? " cc-projects-hero--centered" : "";

  return (
    <section
      ref={sectionRef}
      className={`cc-projects-hero${hasPhotoBackdrop ? " cc-projects-hero--photo" : ""}${layoutClass}`}
      id="projects-hero"
      data-cc-nav="dark"
      {...devSectionAttr("projects-hero")}
    >
      <div className="cc-projects-hero__frame">
        {hasPhotoBackdrop ? (
          <div className="cc-projects-hero__backdrop" aria-hidden="true">
            <CcResponsivePoster
              src={backdropSrc!}
              alt=""
              fill
              first
              sizes="100vw"
              className="cc-projects-hero__backdrop-image"
            />
            <div className="cc-projects-hero__backdrop-scrim" />
          </div>
        ) : (
          <div className="cc-dark-frame__backdrop" aria-hidden="true">
            <div className="cc-dark-frame__glow cc-dark-frame__glow--teal" />
            <div className="cc-dark-frame__glow cc-dark-frame__glow--violet" />
            <div className="cc-dark-frame__pattern" />
          </div>
        )}
        <div className="cc-projects-hero__layout">
          <div className="cc-projects-hero__content">
            <CcSanjayaEyebrow
              as="p"
              theme="on-dark"
              className="cc-projects-hero__eyebrow"
            >
              {eyebrow}
            </CcSanjayaEyebrow>
            <h1 className="cc-projects-hero__title">{title}</h1>
            <p className="cc-projects-hero__description">{description}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
